import type { MutableRefObject } from 'react';
import type { RealtimeEvent } from 'services';
import type { PlanDiffResponse } from '../plan/diff';

type PlanPatchRegistration = {
  token: symbol;
  handler: (data: PlanDiffResponse) => void;
};

export type BlockRealtimeCoordinator = {
  handleBlockInvalidate(event: RealtimeEvent<Record<string, never>>): void;
  handlePlanPatch(event: RealtimeEvent<PlanDiffResponse>): void;
  registerPlanPatchHandler(handler: (data: PlanDiffResponse) => void): () => void;
};

type CoordinatorOptions = {
  revisionRef: MutableRefObject<number | undefined>;
  invalidateBlock: () => void;
  notifyConcurrentUpdate: () => void;
  notifyResync: () => void;
};

/** How an inbound revision relates to the one the block holds. `exact` carries the narrowed value. */
type RevisionOrder = { kind: 'stale' } | { kind: 'gap' } | { kind: 'exact'; revision: number };

/** Campaign-owned ordering and fallback for the two block revision event types. */
export function createBlockRealtimeCoordinator(options: CoordinatorOptions): BlockRealtimeCoordinator {
  let planPatchRegistration: PlanPatchRegistration | null = null;

  const classify = (revision: number | undefined): RevisionOrder => {
    const expected = (options.revisionRef.current || 0) + 1;
    if (!revision || revision < expected) return { kind: 'stale' };
    if (revision > expected) return { kind: 'gap' };
    return { kind: 'exact', revision };
  };

  const ignore = (revision: number | undefined): void => {
    console.warn(`[BlockRealtime] Ignoring stale or invalid revision ${revision ?? 'missing'}`);
  };

  const resync = (): void => {
    options.invalidateBlock();
    options.notifyResync();
  };

  return {
    handleBlockInvalidate(event): void {
      const order = classify(event.revision);
      if (order.kind === 'stale') {
        ignore(event.revision);
        return;
      }
      if (order.kind === 'gap') {
        resync();
        return;
      }

      options.invalidateBlock();
      options.notifyConcurrentUpdate();
      // The HTTP response remains the source of truth and advances revisionRef.
    },

    handlePlanPatch(event): void {
      const order = classify(event.revision);
      if (order.kind === 'stale') {
        ignore(event.revision);
        return;
      }
      if (order.kind === 'gap') {
        resync();
        return;
      }

      if (!planPatchRegistration) {
        // The plan is not mounted. Preserve the existing fallback so the next request is based on
        // the event revision without materializing a plan store solely for realtime.
        options.revisionRef.current = order.revision;
        return;
      }

      try {
        planPatchRegistration.handler(event.data);
        options.revisionRef.current = order.revision;
      } catch (error) {
        console.error('[BlockRealtime] Failed to apply PLAN/PATCH; resynchronizing', error);
        resync();
      }
    },

    registerPlanPatchHandler(handler): () => void {
      const token = Symbol('plan-patch-handler');
      planPatchRegistration = { token, handler };
      return () => {
        if (planPatchRegistration?.token === token) planPatchRegistration = null;
      };
    },
  };
}
